import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES } from "@/data/site";
import { SEO } from "@/components/Seo";

const TEAM = [
  { initials: "R", name: "Renuka", role: "Founder & Nutrition Science", note: "Reads clinical papers so you don't have to." },
  { initials: "DC", name: "Dhiraj Chander", role: "Culinary Director", note: "Believes the tadka is a delivery system for joy." },
  { initials: "DC", name: "Dhiraj Chander", role: "Community & Content", note: "Answers your 2am kanji questions." },
];

const COMMITMENTS = [
  { n: "01", t: "We explain, never prescribe.", d: "Every claim links back to a mechanism in plain language. If the evidence is early, we say so. Your doctor always outranks us." },
  { n: "02", t: "No diet culture. Ever.", d: "No 'guilt-free', no 'cheat days', no body commentary. Food is not morality and health is not a look." },
  { n: "03", t: "Indian first, on purpose.", d: "We build on dal, millets, ferments and the spice box — not imported superfood trends with marketing budgets." },
  { n: "04", t: "Compliant by design.", d: "We follow FSSAI advertising norms strictly: food supports management of conditions; it does not treat, cure or prevent disease. We write like it." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="Our Philosophy | Pranivaa Kitchens"
        description="Food in service of prana. Meet the team and the commitments behind Pranivaa Kitchens — no diet culture, Indian-first, FSSAI-compliant by design."
        path="/about"
      />
      <section className="pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-5">
                Our Philosophy
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-ink font-semibold mb-7">
                Food in service of <em className="text-transparent bg-clip-text bg-gradient-to-r from-beet to-clay-deep">prana</em>.
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-ink-soft mb-5">
                Pranivaa takes its name from Sanskrit roots — nourishment that keeps your prana, your
                life-force, flowing. We started with a frustration: the people who most need clear food
                guidance — young professionals managing blood sugar, hormones, thyroids and guts — get
                either medical pamphlets or influencer fads.
              </p>
              <p className="text-base leading-relaxed text-ink-soft">
                So we are building the third thing: a clean vegetarian and vegan food brand that earns
                your trust with explanation first, and earns your meals later. Content now, kitchens
                next — Delhi NCR and Bengaluru.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-6 relative">
            <div className="absolute -inset-3 bg-clay-light rounded-3xl -rotate-1" aria-hidden="true" />
            <img loading="lazy" decoding="async"
              src={IMAGES.spiceMarket}
              alt="Mounds of colourful Indian spices at a traditional market"
              className="relative rounded-3xl shadow-xl w-full h-[420px] object-cover"
            />
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-3">
              The Pranivaa Approach
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink font-semibold">
              Values, stated as commitments.
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {COMMITMENTS.map((c) => (
              <StaggerItem key={c.n} className="bg-paper-card border border-[#E7E2D7] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <p className="font-serif text-5xl font-semibold text-turmeric/80 leading-none">{c.n}</p>
                <h3 className="font-serif text-xl font-semibold text-ink mt-4 mb-2">{c.t}</h3>
                <p className="text-sm leading-relaxed text-ink-soft">{c.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-paper-muted/60 border-t border-[#E7E2D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-3">
              Meet the Team
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink font-semibold">
              Small team. Large spice rack.
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {TEAM.map((m) => (
              <StaggerItem key={m.role} className="text-center bg-paper-card border border-[#E7E2D7] rounded-2xl p-8">
                <div className="w-20 h-20 mx-auto rounded-full bg-beet text-turmeric font-serif text-2xl font-semibold flex items-center justify-center mb-5">
                  {m.initials}
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink">{m.name}</h3>
                <p className="font-mono text-[11px] tracking-[0.12em] uppercase text-clay font-semibold mt-1 mb-3">{m.role}</p>
                <p className="text-sm text-ink-soft italic">{m.note}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
